// Clinical documents (docs/plans/phase1-consultation.md §3.3 C4, §7.4): sick sheets,
// referral letters and uploaded documents. Titles, remarks, reasons and summaries travel
// only in bodies; URLs carry internal IDs only.
import { queryOptions } from '@tanstack/react-query';
import {
  patientDocumentListSchema,
  patientDocumentSchema,
  referralLetterListSchema,
  referralLetterSchema,
  referralPreviewSchema,
  referralPrintSchema,
  sickSheetListSchema,
  sickSheetPrintSchema,
  sickSheetSchema,
  type IssueReferralRequest,
  type IssueSickSheetRequest,
  type UploadDocumentRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { ApiError, api, OFFLINE_MESSAGE } from './client.ts';

export const documentKeys = {
  visitSickSheets: (visitId: string) => ['visits', visitId, 'sick-sheets'] as const,
  patientSickSheets: (patientId: string) => ['patients', patientId, 'sick-sheets'] as const,
  visitReferrals: (visitId: string) => ['visits', visitId, 'referrals'] as const,
  patientReferrals: (patientId: string) => ['patients', patientId, 'referrals'] as const,
  patientDocuments: (patientId: string) => ['patients', patientId, 'documents'] as const,
  sickSheetPrint: (id: string) => ['sick-sheets', id, 'print'] as const,
  referralPrint: (id: string) => ['referrals', id, 'print'] as const,
};

const copySchema = z.object({ copy: z.number() });

// --- Sick sheets ---------------------------------------------------------------------------

export const visitSickSheetsQuery = (visitId: string) =>
  queryOptions({
    queryKey: documentKeys.visitSickSheets(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/sick-sheets`, { schema: sickSheetListSchema }),
  });

export const patientSickSheetsQuery = (patientId: string) =>
  queryOptions({
    queryKey: documentKeys.patientSickSheets(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/sick-sheets`, { schema: sickSheetListSchema }),
  });

export function issueSickSheet(visitId: string, body: IssueSickSheetRequest) {
  return api('POST', `/visits/${visitId}/sick-sheets`, { body, schema: sickSheetSchema });
}

export const sickSheetPrintQuery = (id: string) =>
  queryOptions({
    queryKey: documentKeys.sickSheetPrint(id),
    queryFn: () => api('GET', `/sick-sheets/${id}/print`, { schema: sickSheetPrintSchema }),
  });

export function markSickSheetPrinted(id: string) {
  return api('POST', `/sick-sheets/${id}/printed`, { schema: copySchema });
}

export function sickSheetEnteredInError(id: string, reason: string) {
  return api('POST', `/sick-sheets/${id}/entered-in-error`, {
    body: { reason },
    schema: sickSheetSchema,
  });
}

// --- Referral letters ----------------------------------------------------------------------

export const visitReferralsQuery = (visitId: string) =>
  queryOptions({
    queryKey: documentKeys.visitReferrals(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/referrals`, { schema: referralLetterListSchema }),
  });

export const patientReferralsQuery = (patientId: string) =>
  queryOptions({
    queryKey: documentKeys.patientReferrals(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/referrals`, { schema: referralLetterListSchema }),
  });

export const referralPreviewQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['visits', visitId, 'referral-preview'] as const,
    queryFn: () =>
      api('POST', `/visits/${visitId}/referrals/preview`, { schema: referralPreviewSchema }),
    staleTime: 0,
  });

export function issueReferral(visitId: string, body: IssueReferralRequest) {
  return api('POST', `/visits/${visitId}/referrals`, { body, schema: referralLetterSchema });
}

export const referralPrintQuery = (id: string) =>
  queryOptions({
    queryKey: documentKeys.referralPrint(id),
    queryFn: () => api('GET', `/referrals/${id}/print`, { schema: referralPrintSchema }),
  });

export function markReferralPrinted(id: string) {
  return api('POST', `/referrals/${id}/printed`, { schema: copySchema });
}

export function referralEnteredInError(id: string, reason: string) {
  return api('POST', `/referrals/${id}/entered-in-error`, {
    body: { reason },
    schema: referralLetterSchema,
  });
}

// --- Uploaded documents --------------------------------------------------------------------

export const patientDocumentsQuery = (patientId: string) =>
  queryOptions({
    queryKey: documentKeys.patientDocuments(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/documents`, { schema: patientDocumentListSchema }),
  });

export function uploadDocument(visitId: string, body: UploadDocumentRequest) {
  return api('POST', `/visits/${visitId}/documents`, { body, schema: patientDocumentSchema });
}

export function documentEnteredInError(id: string, reason: string) {
  return api('POST', `/documents/${id}/entered-in-error`, {
    body: { reason },
    schema: patientDocumentSchema,
  });
}

/** The file as base64, read on the device; nothing leaves it until the upload is sent. */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * The document's bytes, for the viewer or a download. Each fetch is audited on the server:
 * as a chart read when viewing, as an export when downloading (P11).
 */
export async function fetchDocumentContent(id: string, mode: 'view' | 'download'): Promise<Blob> {
  return fetchDocumentBytes(
    `/api/v1/documents/${id}/content${mode === 'download' ? '?download=1' : ''}`,
  );
}

/** Bytes from a document content route, with the server's error message on failure. */
export async function fetchDocumentBytes(url: string): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      typeof body === 'object' &&
      body !== null &&
      'error' in body &&
      typeof body.error === 'object' &&
      body.error !== null &&
      'message' in body.error &&
      typeof body.error.message === 'string'
        ? body.error.message
        : 'The document could not be opened. Try again.';
    throw new ApiError(response.status, 'unexpected_response', message);
  }
  return response.blob();
}
